// Mirror of backend/src/services/lis/caseType/deriveCaseType.ts — keep the two in lockstep.
//
// Case type is derived from specimen materials + case signals; it is never chosen by the user
// and never derived from prefix. This frontend copy powers the live read-only case-type badge.
// The backend re-derives authoritatively on every save and at complete.

export type CaseType =
  | "global"
  | "pass_through"
  | "consultation"
  | "tc_only"
  | "grossing_only";

export interface CaseTypeSignals {
  isConsultation: boolean;
  isTcOnly: boolean;
  isGrossingOnly?: boolean;
}

// Raw materials require full processing at GCP → Global. Everything else arrives processed.
const RAW_MATERIALS = new Set<string>([
  "formalin_jar",
  "blood_tube",
  "urine_sample",
  "stool_collection",
]);

const CASE_TYPE_LABELS: Record<CaseType, string> = {
  global: "Global",
  pass_through: "Pass-Through",
  consultation: "Consultation",
  tc_only: "TC Only",
  grossing_only: "Grossing Only",
};

export function caseTypeLabel(caseType: CaseType): string {
  return CASE_TYPE_LABELS[caseType];
}

/**
 * Derive the case type from the set of specimen materials and the case signals.
 *
 * Rule (signals first, then material):
 *   1. isConsultation → consultation
 *   2. isTcOnly       → tc_only
 *   3. any raw material → global
 *   4. otherwise (all already-processed materials, or none yet) → pass_through
 *
 * Empty materials with no signals defaults to GLOBAL.
 */
export function deriveCaseType(
  materials: Array<string | null | undefined>,
  signals: CaseTypeSignals
): CaseType {
  if (signals.isGrossingOnly) {
    return "grossing_only";
  }
  if (signals.isConsultation) {
    return "consultation";
  }
  if (signals.isTcOnly) {
    return "tc_only";
  }

  const present = materials.filter((m): m is string => !!m);
  if (present.length === 0) {
    return "global";
  }

  return present.some((m) => RAW_MATERIALS.has(m)) ? "global" : "pass_through";
}
